import { type ComponentPropsWithoutRef, forwardRef } from 'react';

import { Typography } from '@workspace/ui/components/Typography';
import { ChevronDown24 } from '@workspace/ui/icons';
import { cn } from '@workspace/ui/lib/utils';

/** Chip-style trigger showing the active interface language */
export interface LanguageChipProps
  extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  /** Short language code shown on the chip, e.g. `EN` */
  label: string;
  /** Flag image URL of the active language */
  flag?: string;
  /** Whether the picker this chip controls is open */
  isOpen?: boolean;
}

export const LanguageChip = forwardRef<HTMLButtonElement, LanguageChipProps>(
  ({ label, flag, isOpen = false, className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-haspopup="menu"
      aria-expanded={isOpen}
      {...props}
      className={cn(
        'inline-flex h-10 shrink-0 cursor-pointer items-center gap-1.5 rounded-full',
        'border-none bg-navigation-surface px-3 text-navigation-text-primary',
        'transition-colors duration-[256ms] ease-main hover:bg-navigation-surface-2',
        className,
      )}
    >
      {flag && (
        <img
          src={flag}
          alt=""
          className="h-4 w-[22px] shrink-0 rounded-1 object-cover"
        />
      )}
      <Typography.Text200Medium as="span">{label}</Typography.Text200Medium>
      <ChevronDown24
        width={16}
        height={16}
        className={cn(
          'shrink-0 transition-transform duration-200',
          isOpen && 'rotate-180',
        )}
      />
    </button>
  ),
);

LanguageChip.displayName = 'LanguageChip';
